import { events, type EventsChannel } from 'aws-amplify/data';
import { IRemoteGame, GameDoc } from './apis/game';
import { PhaseNames } from '../stores/game/concepts/game';
import { extractGameIdentifier, getAuthToken, getNamespace, publishEvent, extractSessionId } from '../utils/amplify-config';
import axios from 'axios';

const API_BASE_URL = process.env.API_ENDPOINT;

type GameEventPayload = Partial<GameDoc> & {
    action?: string;
    phaseName?: string;  // Events use phaseName, store uses currentPhase
};

type SubscriptionPayload = {
    id: string;
    type: 'data';
    event: GameEventPayload;
};

type MeetingRoomPayload = {
    id: string;
    type: 'data';
    event: {
        gameId: string;
        role: string;
        meetingRoomURL: string;
    };
};

/**
 * Amplify Events implementation of IRemoteGame
 * Uses hybrid approach:
 * - Commands (write operations) → REST API + AppSync Events publishing
 * - State updates (read operations) → Amplify Events subscriptions
 */
class AmplifyGame implements IRemoteGame {
    private channels: Map<string, EventsChannel> = new Map();
    private subscriptions: Map<string, any> = new Map();

    constructor() {
        // AWS Amplify handles reconnection automatically
    }



    private async getOrCreateChannel(
        gameId: string
    ): Promise<EventsChannel> {
        console.log('🔌 [AmplifyGame] getOrCreateChannel called for gameId:', gameId);

        if (this.channels.has(gameId)) {
            console.log('✅ [AmplifyGame] Channel already exists, reusing');
            return this.channels.get(gameId)!;
        }

        const gameIdentifier = extractGameIdentifier(gameId);
        const namespace = getNamespace();
        const authToken = getAuthToken();
        const channelName = `${namespace}/game/${gameIdentifier}`;

        console.log('🔌 [AmplifyGame] DEBUG - gameId:', gameId, 'gameIdentifier:', gameIdentifier);
        console.log('🔌 [AmplifyGame] DEBUG - Full channel name:', channelName);

        console.log('🔌 [AmplifyGame] Creating new channel:', {
            gameId,
            gameIdentifier,
            namespace,
            channelName,
            hasAuthToken: !!authToken
        });

        try {
            const channel = await events.connect(channelName, { authToken });
            console.log('✅ [AmplifyGame] Channel connected successfully:', channelName);
            this.channels.set(gameId, channel);
            return channel;
        } catch (error) {
            console.error('❌ [AmplifyGame] Failed to connect to channel:', error);
            throw error;
        }
    }

    /**
     * Fetch current game state via REST API
     */
    async getGameState(gameId: string): Promise<GameDoc | null> {
        try {
            // Use the original gameId (e.g., "TEST01::Team1") for the REST API
            // The API endpoint expects the full gameRecordId format which will be
            // parsed by resolveGameQuery into { sessionId: "TEST01", id: "Team1" }
            console.log('🎮 [AmplifyGame] Fetching initial game state for:', gameId);
            const response = await axios.get(`${API_BASE_URL}/games/${gameId}`);
            console.log('🎮 [AmplifyGame] Fetched initial game state:', response.data);
            return initFullGameDoc(response.data);
        } catch (error) {
            console.error(`Failed to fetch game state for ${gameId}:`, error);
            return null;
        }
    }

    /**
     * Subscribe to game state updates
     */
    onGameSnapshot(gameId: string, cb: (doc: Partial<GameDoc>) => void) {
        console.log('📡 [AmplifyGame] onGameSnapshot called for gameId:', gameId);
        this.unsubscribe(gameId);

        const promise = this.getOrCreateChannel(gameId)
            .then((channel) => {
                console.log('📡 [AmplifyGame] Channel ready, checking subscription map...');

                if (!this.subscriptions.has(gameId)) {
                    console.warn('⚠️ [AmplifyGame] Subscription was removed before channel connected');
                    return;
                }

                console.log('📡 [AmplifyGame] Creating subscription...');
                const subscription = channel.subscribe({
                    next: ({ event: gameDoc }: SubscriptionPayload) => {
                        console.log('📡 [AmplifyGame] ✅ Received game snapshot event!');
                        console.log('📡 [AmplifyGame] Event data:', gameDoc);
                        const processed = processGameEvent(gameDoc);
                        cb(processed);
                    },
                    error: (error: Error) => {
                        console.error(`❌ [AmplifyGame] Error in game channel ${gameId}:`, error);
                        // AWS Amplify will handle reconnection automatically
                    },
                });

                console.log('📡 [AmplifyGame] Subscription created successfully');
                this.subscriptions.set(gameId, subscription);
            })
            .catch((error) => {
                console.error(`❌ [AmplifyGame] Failed to connect to game channel ${gameId}:`, error);
                this.subscriptions.delete(gameId);
                // AWS Amplify will handle reconnection automatically
            });

        this.subscriptions.set(gameId, promise);
        return () => {
            this.unsubscribe(gameId);
        };
    }

    onMeetingRoomSnapshot(
        gameId: string,
        cb: (doc: { gameId: string; role: string; meetingRoomURL: string }) => void
    ) {
        const meetingKey = `${gameId}-meeting`;
        this.unsubscribe(meetingKey);

        const gameIdentifier = extractGameIdentifier(gameId);
        const channelName = `${getNamespace()}/game/${gameIdentifier}/meetings`;

        const setupMeetingSubscription = () => {
            const promise = events.connect(channelName, { authToken: getAuthToken() })
                .then((channel) => {
                    if (!this.subscriptions.has(meetingKey)) return;

                    const subscription = channel.subscribe({
                        next: ({ event }: MeetingRoomPayload) => {
                            cb(event);
                        },
                        error: (error: Error) => {
                            console.error(`Error in meeting room channel:`, error);
                            // AWS Amplify will handle reconnection automatically
                        },
                    });

                    this.subscriptions.set(meetingKey, subscription);
                    this.channels.set(meetingKey, channel);
                })
                .catch((error) => {
                    console.error(`Failed to connect to meeting channel:`, error);
                    this.subscriptions.delete(meetingKey);
                    // AWS Amplify will handle reconnection automatically
                });

            this.subscriptions.set(meetingKey, promise);
        };

        setupMeetingSubscription();

        return () => {
            this.unsubscribe(meetingKey);
        };
    }



    private unsubscribe(key: string) {
        if (this.subscriptions.has(key)) {
            const sub = this.subscriptions.get(key);
            // If it's a subscription object
            if (sub && typeof sub.unsubscribe === 'function') {
                sub.unsubscribe();
            }
            // If it's a pending promise, we just remove it from the map.
            // The .then() block checks the map before subscribing, so it will respect this removal.
            this.subscriptions.delete(key);
        }
    }

    // ===== COMMANDS (REST API + AppSync Events) =====

    async startGame(
        gameId: string,
        startTime: number,
        players: string[]
    ): Promise<void> {
        // 1. Execute REST API call
        await axios.post(`${API_BASE_URL}/games/${gameId}/start`, {
            startTime,
            players,
        });

        // 2. Publish event to AppSync for real-time updates
        const gameIdentifier = extractGameIdentifier(gameId);
        const sessionId = extractSessionId(gameId);

        await publishEvent(`/game/${gameIdentifier}`, {
            action: 'start',
            gameId,
            startTime,
            players,
        });

        // Also publish to session channel for debrief updates
        await publishEvent(`/session/${sessionId}`, {
            type: 'game-updated',
            data: {
                gameId,
                isStarted: true,
                startTime,
                players,
            },
        });
    }

    async startGamePhase(
        gameId: string,
        phaseName: PhaseNames,
        startTime: number = Date.now()
    ): Promise<void> {
        // 1. Execute REST API call
        await axios.post(`${API_BASE_URL}/games/${gameId}/phase`, {
            phaseName,
            time: startTime,
        });

        // 2. Publish event to AppSync for real-time updates
        const gameIdentifier = extractGameIdentifier(gameId);
        const sessionId = extractSessionId(gameId);

        await publishEvent(`/game/${gameIdentifier}`, {
            action: 'phase-started',
            gameId,
            phaseName,
            startTime,
        });

        // Also publish to session channel for debrief updates
        await publishEvent(`/session/${sessionId}`, {
            type: 'game-updated',
            data: {
                gameId,
                currentPhase: phaseName,
                currentPhaseStartTime: startTime,
            },
        });
    }

    async setPlayers(gameId: string, players: string[]): Promise<void> {
        // 1. Execute REST API call
        await axios.put(`${API_BASE_URL}/games/${gameId}/players`, { players });

        // 2. Publish event to AppSync for real-time updates
        const gameIdentifier = extractGameIdentifier(gameId);

        await publishEvent(`/game/${gameIdentifier}`, {
            action: 'players-updated',
            gameId,
            players,
        });
    }

    async openChat(gameId: string): Promise<void> {
        // 1. Execute REST API call
        await axios.put(`${API_BASE_URL}/games/${gameId}/chat/open`);

        // 2. Publish event to AppSync for real-time updates
        const gameIdentifier = extractGameIdentifier(gameId);
        const sessionId = extractSessionId(gameId);

        await publishEvent(`/game/${gameIdentifier}`, {
            action: 'chat-opened',
            gameId,
        });

        // Also publish to session channel for debrief updates
        await publishEvent(`/session/${sessionId}`, {
            type: 'game-updated',
            data: {
                gameId,
                isChatOpen: true,
                isChatReadable: true,
            },
        });
    }

    async closeChat(gameId: string): Promise<void> {
        // 1. Execute REST API call
        await axios.put(`${API_BASE_URL}/games/${gameId}/chat/close`);

        // 2. Publish event to AppSync for real-time updates
        const gameIdentifier = extractGameIdentifier(gameId);
        const sessionId = extractSessionId(gameId);

        await publishEvent(`/game/${gameIdentifier}`, {
            action: 'chat-closed',
            gameId,
        });

        // Also publish to session channel for debrief updates
        await publishEvent(`/session/${sessionId}`, {
            type: 'game-updated',
            data: {
                gameId,
                isChatOpen: false,
            },
        });
    }

    async setRoleMeetingRoom(
        gameId: string,
        role: string,
        meetingRoomURL: string
    ): Promise<void> {
        // 1. Execute REST API call
        await axios.post(`${API_BASE_URL}/games/${gameId}/meeting-room`, {
            role,
            meetingRoomURL,
        });

        // 2. Publish event to AppSync for real-time updates
        const gameIdentifier = extractGameIdentifier(gameId);
        const sessionId = extractSessionId(gameId);

        await publishEvent(`/game/${gameIdentifier}/meetings`, {
            action: 'meeting-room-set',
            gameId,
            role,
            meetingRoomURL,
        });

        // Also publish to session channel for debrief updates
        await publishEvent(`/session/${sessionId}`, {
            type: 'game-updated',
            data: {
                gameId,
                meetingRooms: {
                    [role]: meetingRoomURL,
                },
            },
        });


    }

    /**
     * Clean up all channels and subscriptions
     */
    cleanup(): void {
        this.subscriptions.forEach((sub) => {
            if (sub && typeof sub.unsubscribe === 'function') {
                sub.unsubscribe();
            }
        });
        this.subscriptions.clear();

        this.channels.forEach((channel) => {
            channel.close();
        });
        this.channels.clear();
    }
}

/**
 * Initialize a full GameDoc from API response - fills in defaults for missing fields
 */
const initFullGameDoc = (plain: Partial<GameDoc>): GameDoc => ({
    isStarted: false,
    startTime: 0,
    isStopped: false,
    currentPhase: 'intro',
    currentPhaseStartTime: Date.now(),
    players: [],
    isChatReadable: false,
    isChatOpen: false,
    meetingRooms: {
        DS: null,
        DF: null,
        DM: null,
        DP: null,
        PE: null,
        CA: null,
    },
    ...plain,
});

/**
 * Process a game event payload.
 * Maps event-specific field names to GameDoc field names.
 */
const processGameEvent = (payload: GameEventPayload): Partial<GameDoc> => {
    const { action, ...fields } = payload;

    // Map phaseName to currentPhase (event uses phaseName, store uses currentPhase)
    const result: Partial<GameDoc> = { ...fields };
    if ('phaseName' in payload && payload.phaseName) {
        result.currentPhase = payload.phaseName as GameDoc['currentPhase'];
    }
    // Map startTime to currentPhaseStartTime for phase events
    if (action === 'phase-started' && 'startTime' in payload) {
        result.currentPhaseStartTime = payload.startTime as number;
    }

    return result;
};

export const game = new AmplifyGame();